import { useEffect, useState, useMemo, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DealChurnReason, DealStage, UserRole, type DealSummaryDto } from '@bhops/shared';
import { dealsApi, type TransitionDealBody } from '@/api/deals';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { Icon } from '@/components/Icon';
import { useAuthStore } from '@/stores/auth';
import { invalidateCustomerMasterCascade } from '@/lib/invalidate-customer-master-cascade';

// DealTransitionModal — design §4.2.
//
// Opens when the operator drags a Kanban card to a different
// column OR clicks "단계 변경" on the detail page. Shows a
// client-side SM-guard preview so the operator gets immediate
// feedback before submitting (e.g. "PROPOSAL → NEGOTIATION
// requires ≥ 1 DealActivity. Current count: 0").
//
// Server is the source of truth — guard preview is best-effort
// UX. A 422 response on submit re-renders the server's error
// message in-modal.

const STAGE_LABEL: Record<DealStage, string> = {
  LEAD: '리드',
  QUALIFIED: '검증',
  PROPOSAL: '제안',
  NEGOTIATION: '협상',
  ACTIVE: '계약 체결',
  CHURNED: '이탈',
};

const CHURN_REASON_LABEL: Record<DealChurnReason, string> = {
  COST: '예산 초과',
  TIMING: '지금은 도입할 때가 아님',
  COMPETITOR: '경쟁사로 이탈',
  NO_RESPONSE: '응답 없음',
  OTHER: '기타',
};

// Client-side mirror of the SM transition table from
// `apps/api/src/deals/state-machine/deal-state-machine.ts`.
// Server is the source of truth; this is only used to disable
// invalid Kanban DnD targets + render a structural preview.
const ALLOWED_TRANSITIONS: Record<DealStage, ReadonlySet<DealStage>> = {
  LEAD: new Set([DealStage.QUALIFIED, DealStage.CHURNED]),
  QUALIFIED: new Set([DealStage.PROPOSAL, DealStage.CHURNED]),
  PROPOSAL: new Set([DealStage.NEGOTIATION, DealStage.CHURNED]),
  NEGOTIATION: new Set([DealStage.ACTIVE, DealStage.CHURNED]),
  ACTIVE: new Set([DealStage.CHURNED]),
  CHURNED: new Set(),
};

export function canTransition(from: DealStage, to: DealStage): boolean {
  return ALLOWED_TRANSITIONS[from]?.has(to) ?? false;
}

// round3 #8e — 백엔드 isRoleAllowedForTransition(deal-state-machine.ts) 의 프론트 미러.
// ACTIVE(계약 체결)는 '서버 전용' — 계약 생성·서명 시에만 자동 발생하므로 직접 전이는
// ADMIN 만. 이 규칙을 미러링하지 않으면 비-ADMIN 이 '→ 계약 체결' 버튼/칸반 드롭으로
// 시도 → 서버가 422('Role X cannot transition Deal to ACTIVE'). VIEWER 는 모든 전이 불가.
export function isStageTransitionRoleAllowed(to: DealStage, role: UserRole | undefined): boolean {
  if (to === DealStage.ACTIVE) return role === UserRole.ADMIN;
  if (!role || role === UserRole.VIEWER) return false;
  return true;
}

export interface GuardPreview {
  ok: boolean;
  message: string | null;
}

/**
 * Mirrors the server-side BR-DEAL-01 guards for early operator
 * feedback. Server runs the same checks authoritatively — this is
 * UX, not enforcement.
 *
 * Exported for unit tests; hot path stays in this module.
 */
export function previewGuards(deal: DealSummaryDto, to: DealStage): GuardPreview {
  if (deal.stage === DealStage.LEAD && to === DealStage.QUALIFIED) {
    if (deal.expectedRevenue == null && deal.expectedRobotCount == null) {
      return {
        ok: false,
        message: '검증 단계로 넘기려면 예상 매출이나 로봇 대수 중 하나를 먼저 입력해 주세요.',
      };
    }
  }
  if (deal.stage === DealStage.PROPOSAL && to === DealStage.NEGOTIATION) {
    const count = deal._count?.activities ?? 0;
    if (count < 1) {
      return {
        ok: false,
        message: `협상 단계로 넘기려면 활동 기록이 1건 이상 있어야 합니다. 지금은 ${count}건입니다.`,
      };
    }
  }
  // CHURNED reason check is not a structural guard — it's a
  // form-level required field. Handled inline by disabling submit
  // until the operator picks a reason.
  return { ok: true, message: null };
}

interface Props {
  open: boolean;
  onClose: () => void;
  deal: DealSummaryDto;
  to: DealStage;
}

export function DealTransitionModal({ open, onClose, deal, to }: Props) {
  const toast = useToast();
  const qc = useQueryClient();

  const [reason, setReason] = useState('');
  const [churnReason, setChurnReason] = useState<DealChurnReason>(DealChurnReason.OTHER);
  const [churnNote, setChurnNote] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setChurnReason(DealChurnReason.OTHER);
      setChurnNote('');
      setSubmitError(null);
    }
  }, [open, deal.id, to]);

  const role = useAuthStore((s) => s.user?.role);
  const guard = useMemo(() => previewGuards(deal, to), [deal, to]);
  const tableAllowed = canTransition(deal.stage, to);
  const isChurn = to === DealStage.CHURNED;
  // round3 #8e — role 게이트(ACTIVE=ADMIN 전용) 위반 시 안내 메시지로 guard 를 덮어
  // 제출을 막는다. 서버 422 를 사전 차단하고 정상 경로(계약 생성)를 안내.
  const roleAllowed = isStageTransitionRoleAllowed(to, role);
  const effectiveGuard: GuardPreview = !roleAllowed
    ? {
        ok: false,
        message:
          to === DealStage.ACTIVE
            ? '계약 체결 단계는 계약을 만들고 서명하면 자동으로 넘어갑니다. 직접 바꾸는 것은 관리자만 할 수 있습니다.'
            : '이 단계로 바꿀 권한이 없습니다.',
      }
    : guard;

  const transition = useMutation({
    mutationFn: (body: TransitionDealBody) => dealsApi.transition(deal.id, body),
    onSuccess: () => {
      // round4 #5a (BUG#1) — CHURNED 는 연결 계약을 TERMINATE(BR-DEAL-CASCADE)하므로
      // contracts/customer-master/deployments/dashboard 까지 일괄 무효화.
      invalidateCustomerMasterCascade(qc);
      qc.invalidateQueries({ queryKey: ['deals', deal.id] });
      toast.success(`${deal.humanId} 딜을 ${STAGE_LABEL[to]} 단계로 옮겼습니다.`, 'Deal 단계 변경');
      onClose();
      setReason('');
      setChurnNote('');
    },
    onError: (err) => setSubmitError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setSubmitError(null);
    const body: TransitionDealBody = {
      to,
      reason: reason || undefined,
      ...(isChurn ? { churnReason, churnNote: churnNote || undefined } : {}),
    };
    transition.mutate(body);
  }

  const submitDisabled = !tableAllowed || !effectiveGuard.ok || transition.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Deal 단계 변경: ${STAGE_LABEL[deal.stage]} → ${STAGE_LABEL[to]}`}
      width={500}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="submit"
            form="deal-transition-form"
            variant={isChurn ? 'danger' : 'primary'}
            disabled={submitDisabled}
          >
            {transition.isPending ? '처리 중…' : '확인'}
          </Button>
        </>
      }
    >
      <form id="deal-transition-form" onSubmit={submit} noValidate>
        <FormError>{submitError}</FormError>

        <div
          style={{
            padding: 10,
            marginBottom: 14,
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-card)',
            background: 'var(--bg-sunken)',
            fontSize: 12,
          }}
        >
          <div
            style={{
              fontWeight: 500,
              marginBottom: 4,
              fontFamily: 'var(--font-mono)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {deal.humanId}
          </div>
          <div style={{ color: 'var(--fg-muted)' }}>
            {deal.customer.displayName}
            {deal.store && ` · ${deal.store.name}`}
          </div>
        </div>

        {!tableAllowed && (
          <div style={errorBanner}>
            {STAGE_LABEL[deal.stage]} 단계에서 {STAGE_LABEL[to]} 단계로는 바꿀 수 없습니다.
          </div>
        )}

        {tableAllowed && !effectiveGuard.ok && (
          <div style={errorBanner}>
            <Icon name="alert" size={14} aria-hidden /> {effectiveGuard.message}
          </div>
        )}

        {isChurn && (
          <>
            <Field label="이탈 사유" htmlFor="t-churn-reason" required>
              <Select
                id="t-churn-reason"
                value={churnReason}
                onChange={(e) => setChurnReason(e.target.value as DealChurnReason)}
              >
                {Object.entries(CHURN_REASON_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="이탈 메모" htmlFor="t-churn-note">
              <Textarea
                id="t-churn-note"
                rows={2}
                value={churnNote}
                onChange={(e) => setChurnNote(e.target.value)}
                placeholder="구체적인 이탈 정황을 적어 주세요"
              />
            </Field>
          </>
        )}

        <Field
          label="메모"
          htmlFor="t-reason"
          hint="단계 변경 이력에 함께 남습니다. 비워 둬도 됩니다."
        >
          <Textarea
            id="t-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>

        {isChurn && (
          <div style={cascadeWarning}>
            <Icon name="alert" size={14} aria-hidden style={{ marginTop: 2 }} /> 이탈로 바꾸면
            연결된 활성 계약과 초안 계약이 자동으로 해지됩니다.
          </div>
        )}
      </form>
    </Modal>
  );
}

const errorBanner = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: 10,
  marginBottom: 14,
  border: '1px solid var(--fg-danger)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  fontSize: 12,
} as const;

const cascadeWarning = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
  marginTop: 8,
  padding: 10,
  border: '1px dashed var(--border-warning-saturated)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-default)',
  fontSize: 12,
} as const;
